import { useMemo } from 'react';

import { Box, Typography } from '@mui/material';

import { DocumentUploadedPreview } from 'src/components/DocumentUploadedPreview';
import { TabSelectCard } from 'src/components/TabSelectCard';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';

import { Principals } from './components/Principals';
import { SupportingDocumentsView } from './components/ProofOfBank';

export const PrincipleSection: React.FC = () => {
    const { merchantApplication } = useAddMerchantContext();
    const data = useMemo(() => {
        const principalDocuments =
            merchantApplication?.supportingDocuments?.principalDocuments ?? [];
        const principals = principalDocuments
            .filter((doc, index) => {
                const principalInfo = merchantApplication?.merchantInformation?.principals?.[index];

                return principalInfo?.isBeneficialOwner;
            })
            .map(({ firstName, lastName, addressFile, idFile }) => {
                return {
                    firstName,
                    lastName,
                    personalDocuments: [
                        {
                            fileName:
                                addressFile?.fileName ||
                                addressFile?.fileType ||
                                'Proof Of Address',
                            url: addressFile?.locationUrl,
                            size: `${addressFile?.fileSize} KB`,
                        },
                        {
                            fileName:
                                idFile?.settlementAccountFile || idFile?.fileType || 'Proof Of Id',
                            url: idFile?.locationUrl,
                            size: `${idFile?.fileSize} KB`,
                        },
                    ],
                };
            });

        const settlementAccountFile =
            merchantApplication?.supportingDocuments?.proofOfBank?.settlementAccountFile;
        const chargeableAccountFile =
            merchantApplication?.supportingDocuments?.proofOfBank?.chargeableAccountFile;
        const outletFiles =
            merchantApplication?.supportingDocuments?.proofOfBank?.outletFiles ?? [];

        const proofOfBank = {
            settlementBank: {
                url: settlementAccountFile?.locationUrl,
                fileName: settlementAccountFile?.fileName || settlementAccountFile?.fileType,
                size: settlementAccountFile?.fileSize
                    ? `${settlementAccountFile?.fileSize} KB`
                    : '',
            },
            chargeableBank: {
                url: chargeableAccountFile?.locationUrl,
                fileName: chargeableAccountFile?.fileName || chargeableAccountFile?.fileType,
                size: chargeableAccountFile?.fileSize
                    ? `${chargeableAccountFile?.fileSize} KB`
                    : '',
            },
            outlet2: {
                url: outletFiles?.map(({ locationUrl }) => locationUrl),
                fileName:
                    outletFiles?.map(({ fileName }) => fileName) ||
                    outletFiles?.map(({ fileType }) => fileType),
                size: outletFiles.map(({ fileSize }) => fileSize + ' KB'),
            },
        };

        const supportingDocuments = [];

        if (merchantApplication?.supportingDocuments?.otherFile) {
            supportingDocuments.push({
                url: merchantApplication?.supportingDocuments?.otherFile.locationUrl,
                fileName:
                    merchantApplication?.supportingDocuments?.otherFile?.fileName ||
                    merchantApplication?.supportingDocuments?.otherFile?.fileType ||
                    'Other',
                size: merchantApplication?.supportingDocuments?.otherFile?.fileSize
                    ? `${merchantApplication?.supportingDocuments?.otherFile?.fileSize} KB`
                    : '',

                fileType: merchantApplication?.supportingDocuments?.otherFile?.fileType || 'Other',
            });
        }

        if (merchantApplication?.supportingDocuments?.businessReportFile) {
            supportingDocuments.push({
                url: merchantApplication?.supportingDocuments?.businessReportFile.locationUrl,
                fileName:
                    merchantApplication?.supportingDocuments?.businessReportFile?.fileName ||
                    merchantApplication?.supportingDocuments?.businessReportFile?.fileType ||
                    'Business Report',
                size: merchantApplication?.supportingDocuments?.businessReportFile?.fileSize
                    ? `${merchantApplication?.supportingDocuments?.businessReportFile?.fileSize} KB`
                    : '',

                fileType:
                    merchantApplication?.supportingDocuments?.businessReportFile?.fileType ||
                    'Business Report',
            });
        }

        if (merchantApplication?.supportingDocuments?.b1FormFile) {
            supportingDocuments.push({
                url: merchantApplication?.supportingDocuments?.b1FormFile.locationUrl,
                fileName:
                    merchantApplication?.supportingDocuments?.b1FormFile?.fileName ||
                    merchantApplication?.supportingDocuments?.b1FormFile?.fileType ||
                    'B1 Form',
                size: merchantApplication?.supportingDocuments?.b1FormFile?.fileSize
                    ? `${merchantApplication?.supportingDocuments?.b1FormFile?.fileSize} KB`
                    : '',

                fileType:
                    merchantApplication?.supportingDocuments?.b1FormFile?.fileType || 'B1 Form',
            });
        }

        return { principals, proofOfBank, supportingDocuments };
    }, [merchantApplication]);

    const formattedOutlet2Data = {
        fileName: data.proofOfBank.outlet2.fileName,
        url: data.proofOfBank.outlet2.url,
        size: data.proofOfBank.outlet2.size,
    };

    const TABS = useMemo(() => {
        const dataOfOutlet = formattedOutlet2Data;

        const mappedData = dataOfOutlet?.fileName?.map((fileName, index) => ({
            label: `Outlet ${index + 1}`,
            file: {
                fileName: fileName,
                url: dataOfOutlet.url[index],
                size: dataOfOutlet.size[index],
            },
        }));

        return [
            {
                header: 'Principals',
                children: <Principals principals={data.principals} />,
            },
            {
                header: 'Proof of Bank(s)',
                children: (
                    <SupportingDocumentsView
                        heading="Proof of Bank(s)"
                        documents={[
                            {
                                label: 'Settlement Bank',
                                file: data.proofOfBank.settlementBank,
                            },
                            {
                                label: 'Chargeable Bank',
                                file: data.proofOfBank.chargeableBank,
                            },
                            ...mappedData,
                        ]}
                    />
                ),
            },
            {
                header: 'Supporting Documents',
                children: (
                    <Box display="flex" flexDirection="row" gap="20px">
                        {data.supportingDocuments.length ? (
                            data.supportingDocuments.map((document, index) =>
                                document.url ? (
                                    <DocumentUploadedPreview
                                        key={index}
                                        file={document}
                                        label={document?.fileType}
                                    />
                                ) : null
                            )
                        ) : (
                            <Typography>No Documents have been uploaded</Typography>
                        )}
                    </Box>
                ),
            },
        ];
    }, [data]);

    return <TabSelectCard tabs={TABS} />;
};
